import React from 'react';

import PropTypes from 'prop-types';
import { t } from '@lingui/macro';
import {
  Button,
  Checkbox,
  Flex,
  Select,
  TextField,
} from '@decisiv/ui-components';
import Message from '@decisiv/ui-components/lib/components/Message';
import MinusCircle from '@decisiv/iconix/lib/components/MinusCircle';
import PlusCircle from '@decisiv/iconix/lib/components/PlusCircle';

const HTML_TAG_OPTIONS = [
  { id: 'text_input', label: 'Text Input' },
  { id: 'password_input', label: 'Password Input' },
];

const SNAKE_CASE_RE = /^[a-z][a-z0-9_]*$/;

const isValidAttributeName = (name) => !name || SNAKE_CASE_RE.test(name);

const isDefinitionsEmpty = (defs) =>
  defs[defs.length - 1]?.attribute_name === '';

const hasInvalidAttributeName = (defs) =>
  defs.some((d) => !isValidAttributeName(d.attribute_name));

const ROW_STYLE = {
  display: 'grid',
  gridTemplateColumns: 'auto 11.25rem 12.5rem 10rem auto',
  alignItems: 'center',
  columnGap: '1.25rem',
  margin: '-1px',
  border: '1px solid var(--if-border)',
  padding: '0.625rem',
};

export default function EditLocationSettings({
  definitions,
  handlers,
  editDisabled = true,
  fieldName,
}) {
  return (
    <>
      {definitions.map((item, index) => {
        const nameInvalid =
          item.attribute_name && !isValidAttributeName(item.attribute_name);
        return (
          // eslint-disable-next-line react/no-array-index-key
          <div key={`definition-row-${index}`} style={ROW_STYLE}>
            <Button
              aria-label={t`Remove entry`}
              variant="ghost"
              icon={MinusCircle}
              disabled={editDisabled}
              onClick={() => handlers.removeEntry(index, fieldName)}
            />
            <TextField
              required
              value={item.attribute_name || ''}
              label={t`attribute name`}
              name={`attribute_name-${index}`}
              style={{ marginTop: '0.5rem', width: '11.25rem' }}
              maxLength={50}
              disabled={editDisabled}
              onChange={(e) =>
                handlers.changeValue({ e, item, index, fieldName })
              }
            />
            <TextField
              value={item.description || ''}
              label={t`description`}
              name={`description-${index}`}
              style={{ marginTop: '0.5rem', width: '12.5rem' }}
              maxLength={200}
              disabled={editDisabled}
              onChange={(e) =>
                handlers.changeValue({ e, item, index, fieldName })
              }
            />
            <Select
              name={`html_tag-${index}`}
              label={t`type`}
              value={item.html_tag || 'text_input'}
              style={{ marginTop: '0.5rem', width: '10rem' }}
              disabled={editDisabled}
              options={HTML_TAG_OPTIONS}
              // Under createRoot, KDS's document-level click-outside listener
              // sees the dropdown already open and closes it on the same click.
              onClick={(e) => e.stopPropagation()}
              onChange={(selectedId) =>
                handlers.changeValue({
                  item,
                  index,
                  fieldName,
                  field: 'html_tag',
                  selectedId,
                })
              }
            />
            <Checkbox
              disabled={editDisabled}
              name={`required-${index}`}
              label={t`Required`}
              value="required"
              checked={item.required || false}
              onChange={(e) =>
                handlers.changeValue({ e, item, index, fieldName })
              }
            />
            {nameInvalid && (
              <Message
                intent="warning"
                style={{ gridColumn: 2, marginTop: '0.5rem' }}
              >
                {t`Use lowercase letters, digits, and underscores (e.g. dealer_code)`}
              </Message>
            )}
          </div>
        );
      })}
      <Flex
        justifyContent="flex-start"
        alignItems="center"
        style={{
          border: '1px solid var(--if-border)',
          margin: '-1px',
          gap: '1.25rem',
          padding: '0.625rem',
        }}
      >
        <Button
          aria-label={`${t`Add`} ${fieldName}`}
          text={`${t`Add`} ${fieldName}`}
          icon={PlusCircle}
          variant="ghost"
          disabled={
            editDisabled ||
            isDefinitionsEmpty(definitions) ||
            hasInvalidAttributeName(definitions)
          }
          onClick={() => handlers.addEntry({ fieldName })}
        />
      </Flex>
    </>
  );
}

EditLocationSettings.propTypes = {
  definitions: PropTypes.arrayOf(
    PropTypes.shape({
      attribute_name: PropTypes.string,
      description: PropTypes.string,
      html_tag: PropTypes.oneOf(['text_input', 'password_input']),
      required: PropTypes.bool,
    }),
  ).isRequired,
  handlers: PropTypes.shape({
    addEntry: PropTypes.func.isRequired,
    removeEntry: PropTypes.func.isRequired,
    changeValue: PropTypes.func.isRequired,
  }).isRequired,
  fieldName: PropTypes.string.isRequired,
  editDisabled: PropTypes.bool,
};
